import type { CrmSendLiveDto, SendFeedItemDto, SendProgressDto, SendTotalsDto } from "@nexxt/contracts/crm";
import { SEND_LIVE } from "@nexxt/contracts/crm";

// The live control room of a send (ADR-316 §6), pure: what one read adds to what the screen holds, the reads that a
// signal asks for while another is running made into one, the sentence of the polite region at most once a minute, and
// the share of the emails not delivered. No timer anywhere: the server's signals give the pace.

/** What the control room holds: the newest answer, the feed it built read after read, and what it last announced. */
export interface SendLiveState {
  progress: SendProgressDto;
  totals: SendTotalsDto;
  minutes: CrmSendLiveDto["minutes"];
  /** The human signals, the newest first, each once. */
  feed: SendFeedItemDto[];
  cursor: string | null;
  /** When the last read came back: what "2 min ago" counts from. */
  readAt: number;
  /** The progress the polite region says, and when it last changed. */
  announced: { done: number; total: number; at: number };
}

/** How often the polite region may speak (ADR-316 §6): never on the counters, at most once a minute. */
export const ANNOUNCE_EVERY_MS = 60_000;

/** The signals of `incoming` added to `feed`: each once by id, the newest first, at most `SEND_LIVE.feedSize`. */
export function mergeFeed(
  feed: readonly SendFeedItemDto[],
  incoming: readonly SendFeedItemDto[],
  size: number = SEND_LIVE.feedSize,
): SendFeedItemDto[] {
  const byId = new Map(feed.map((item) => [item.id, item]));
  for (const item of incoming) byId.set(item.id, item);
  return [...byId.values()]
    .sort((a, b) => (a.at === b.at ? b.id.localeCompare(a.id) : Date.parse(b.at) - Date.parse(a.at)))
    .slice(0, size);
}

/**
 * How far the send went, as the polite region and "Sending now" of Today say it: done (sent, left out or failed) of the
 * people it goes to, so the two places never disagree.
 */
export function doneOf(
  progress: Pick<SendProgressDto, "recipientCount" | "sentCount" | "skippedCount" | "failedCount">,
): { done: number; total: number } {
  const done = progress.sentCount + progress.skippedCount + progress.failedCount;
  return { done, total: Math.max(progress.recipientCount, done) };
}

/** The state after a read: the answer's numbers, its signals added, the announcement moved only once a minute. */
export function mergeLive(before: SendLiveState | undefined, answer: CrmSendLiveDto, now: number): SendLiveState {
  const progress = doneOf(answer.progress);
  const last = before?.announced;
  const changed = !last || last.done !== progress.done || last.total !== progress.total;
  const due = !last || now - last.at >= ANNOUNCE_EVERY_MS;
  return {
    progress: answer.progress,
    totals: answer.totals,
    minutes: answer.minutes,
    feed: mergeFeed(before?.feed ?? [], answer.feed),
    cursor: answer.cursor ?? before?.cursor ?? null,
    readAt: now,
    announced: changed && due ? { ...progress, at: now } : (last ?? { ...progress, at: now }),
  };
}

/**
 * `read` once at a time: a call while a read runs asks for one more read after it, however many calls came (ADR-316
 * §6: the signals of a read become one read after it). Returns the call.
 */
export function coalesced(read: () => Promise<unknown>): () => void {
  let running = false;
  let again = false;
  const call = (): void => {
    if (running) {
      again = true;
      return;
    }
    running = true;
    void read()
      .catch(() => {})
      .finally(() => {
        running = false;
        if (again) {
          again = false;
          call();
        }
      });
  };
  return call;
}

/** Emails the provider will not deliver: hard bounces, refusals, and those it never took. */
export const notDeliveredOf = (totals: SendTotalsDto, progress: Pick<SendProgressDto, "failedCount">): number =>
  totals.bouncedHard + totals.rejected + progress.failedCount;

/** A share of the emails sent that is a healthy list (a common rule of the providers), below the breaker's 5%. */
export const HEALTHY_NOT_DELIVERED = 0.03;

/** How long a send took, in whole minutes; null while it has not both started and ended. */
export function tookMinutes(progress: Pick<SendProgressDto, "startedAt" | "sentAt">): number | null {
  if (!progress.startedAt || !progress.sentAt) return null;
  return Math.max(1, Math.round((Date.parse(progress.sentAt) - Date.parse(progress.startedAt)) / 60_000));
}
